<script>
/**
 * UsuarioexpedienteQuicktable
 * 
 * @author Daniel Lepe
 * @date May 22th, 2025
 * @version 1.0
 */
import _ from 'lodash'
import { captions, quicktableFilters, baseIndex as fetchingAPI } from './archetype.js'
import { objectToUrlString } from '@/utils.js'
// import moduleNavigator from '../moduleNavigator.vue'

import QuickCardFooter from '@/views/elements/quick-card-footer.vue'
import axios from 'axios'
import automodal from './UsuarioexpedienteAutomodal.vue'

let name = 'UsuarioexpedienteQuicktable',
	newButtonLabel = 'New',
	emptyHeader = 'Data not found',
	emptyLead = 'Module empty',
	reloadData = 'Try reloading this page',
	tryAgainLater = '',
	header = null,
	footer = null;

export default {
	name,

	components: {
		automodal,
		QuickCardFooter,
		// moduleNavigator
	},

	props: {
		usuarioId: {
			type: [String, Number],
			default: null
		},
		value: {
			type: [Object, Number, String]
		},
		valueName: {
			type: String,
			default: 'id'
		},
		disableNew: {
			type: Boolean,
			default: false
		},
		selectMode: {
			type: Boolean,
			default: false
		},
		hideNavigation: {
			type: Boolean,
			default: false
		},
		fetchingAPI: {
			type: String,
			default: () => { return fetchingAPI }
		},
		header: {
			type: String,
			default: () => { return header }
		},
		perPage: {
			type: Number,
			default: 10,
		},
		footer: {
			type: String,
			default: () => { return footer }
		},
		captions: {
			type: [Array, Object],
			default: () => captions
		},
		args: {
			type: Object,
			default: () => { return {} }
		}
	},

	computed: {
		cArgs() {
			if (!this.usuarioId)
				return this.args
			return {
				...this.args,
				usuario_id: this.usuarioId
			}
		},
		cCaptions() {
			let r = [];
			try {
				if (this.selectMode) {
					r.push({
						key: 'check',
						label: ''
					})
				}
				_.forEach(this.captions, item => {
					r.push(item)
				})
			} catch (e) {
				console.error(e)
			}
			return r;
		},
		sourceURI() {
			let init = this.fetchingAPI + "/" + this.perPage + "/" + this.currentPage;
			try {
				if (this.term) {
					init += "/" + this.term;
				} else if (!this.term || this.term == "s") {
					init += "/@";
				}
				const fullArgs = _.merge(this.cArgs, this.filters);
				init += '/' + objectToUrlString(fullArgs)
				return init.trim().replace(/[ ]/g, '_');
			} catch (e) {
				console.warn("sourceURI.building.error", init, e)
			}
			return init;
		}
	},

	data: () => {
		return {
			name, newButtonLabel, emptyHeader,
			emptyLead, tryAgainLater, reloadData,
			currentPage: 1,
			items: [],
			groupingDefinitions: {},
			itemsCount: 0,
			term: null, filters: {},
			isLoading: false
		}
	},

	methods: {
		isSelected(data){
			// console.log('isSelected', data, this.value, (this.value == data.item[this.valueName]))
			return (this.value == data.item[this.valueName])
		},
		rowClicked(item, index, event) {
			this.$emit('setItem', item, event)
			this.$nextTick(() => {
				if (this.selectMode)
					this.$emit('input', item[this.valueName], { item, index, event })
			})
		},
		quicktableFilters,
		async refreshFromStart() {
			this.currentPage = 1
			this.refresh()
		},
		async refresh() {
			this.isLoading = true
			await this.loadUp()
			this.isLoading = false
		},
		async loadUp() {
			try {
				let response = await axios.get(this.sourceURI)
				this.items = response.data.rows;
				this.itemsCount = response.data.count;
				this.groupingDefinitions = (_.isObjectLike(response.groupingDefinitions) ? response.groupingDefinitions : {});
			} catch (error) {
				this.items = [];
				this.itemsCount = 0;
				console.error(error)
			}
		}
	},

	watch: {
		currentPage(a, b) {
			this.refresh();
		},
		'$route'() {
			this.loadUp()
		}
	},

	mounted() {
		this.refresh()
	}
};
</script>

<template>
	<div :id="name">

		<!-- <module-navigator class="mb-3" v-if="!hideNavigation" /> -->

		<b-card no-body>

			<!-- TABLE CONTENT -->
			<b-table  responsive class="animated fadeIn" v-if="items.length && !isLoading" 
				@row-clicked="rowClicked" 
				:hover="selectMode" 
				:items="items" 
				:fields="cCaptions">

				<template #cell(check)="data">
					<div>
						<input type="checkbox" :checked="isSelected(data)" @click.prevent="()=> {}"/>
					</div>
				</template>

				<template #cell(costo)="data">
					{{ data.value | currency }}
				</template>

				<template #cell(imagenes_id)="data">
					<api-img-pristine :comp="data.value" size="128/128" :thumbnail="true" :allow-zoom="true" />
				</template>

				<template #cell(_)="data">
					<automodal :concept-id="data.item.id" @refresh="refresh" :usuario-id="usuarioId" :args="args">
						Editar
					</automodal>
				</template>
				
				<template #cell(expediente_version_title)="data">
					<strong>{{ data.value }}</strong>
				</template>

				<template #cell(id)="data">
					<strong>{{ data.value }}</strong>
				</template>

				<template #cell(usuario)="data">
					<strong>{{ data.value }}</strong>
				</template>

				<template #cell(created)="data">
					<strong>{{ data.value | humanizar_fecha_simple }}</strong>
					<br><small class="text-muted">Última actualización {{ data.item.modified | humanizar_diferencia }}</small>
				</template>

			</b-table>

			<!-- LOADING SPINNER -->

			<div v-if="isLoading">
				<div class="text-center p-5">
					<b-spinner variant="primary" />
				</div>
			</div>

			<!-- EMPTY JUMBOTRON -->

			<div v-else-if="!items.length" class="pt-4 pl-4 pr-4 pb-0">
				<b-jumbotron :header="emptyHeader" :lead="emptyLead">
					<p>{{ tryAgainLater }}</p>
					<b-button variant="primary" @click="refresh()">{{ reloadData }}</b-button>
				</b-jumbotron>
			</div>

			<!-- HEADER -->

			<template v-slot:header>
				<b-row align-h="between">

					<b-col md="auto" v-if="!!header">
						<h6 class="mt-2 mb-2">{{ header }}</h6>
					</b-col>

					<b-col>
						<b-input-group>
							<b-form-input v-model="term" @keyup.enter="refreshFromStart()" placeholder="Search" />
							<b-input-group-append>
								<b-button v-if="!!term" variant="secondary" @click="term = ''; refreshFromStart();"><i
										class="fa fa-times"></i></b-button>
								<b-button variant="secondary" @click="refreshFromStart()" :disabled="!term"><i
										class="fa fa-search"></i></b-button>
							</b-input-group-append>
						</b-input-group>
					</b-col>

					<quicktable-filters v-model="filters" @changed="refreshFromStart" :filters="quicktableFilters" :groupingDefinitions="groupingDefinitions"/>

					<b-col md="auto" class="align-right">
						<automodal @refresh="refresh" :args="args" :usuario-id="usuarioId" v-if="!disableNew">
							Nuevo
						</automodal>
					</b-col>

				</b-row>
			</template>

			<!-- FOOTER -->

			<template v-slot:footer v-if="!isLoading && items.length">
				<quick-card-footer :items-count="itemsCount" :per-page="perPage" v-model="currentPage"
					@refresh="refresh" :items-fetched="items.length" />
			</template>

		</b-card>

	</div>
</template>